import {
  Box,
  Chip,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { ReadOnlyField } from "../../../components/form/ReadOnlyField";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { GetPatient } from "../../../services/fhir";
import { useDatetime } from "../../../utils/useDatetime";
import { useFormatter } from "../../../utils/useFormatter";
import {
  ExpandLess,
  ExpandMore,
  WhatsApp,
} from "@mui/icons-material";
import { useSessionContext } from "../../../contexts/SessionContext";
import { useFhirData } from "../../../utils/useFhirData";
import { lastEncounterObservationsData } from "./observation";
import dayjs from "dayjs";
import { useInternationalVerify } from "../../../utils/useInternationalVerify";
import { currentFeatures } from "@/configs/Settings";
import { GetVetPatient } from "@/services/fhir/vetpatient/GetVetPatient";
import { useEhrRecordsMobileLayout } from "../context/EhrRecordsLayoutContext";
import {
  EhrPatientHeaderMobile,
  EhrPatientPhoneValue,
} from "./mobile/EhrPatientHeaderMobile";

function patientInitials(name?: string): string {
  if (!name?.trim()) return "?";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

export function PatientData({ patientId, summaryData }: any) {
  const { access, user, userPatient } = useSessionContext();
  const navigate = useNavigate();
  const isEhrMobile = useEhrRecordsMobileLayout();
  const [patientData, setPatientData] = useState<fhir5.Patient>();
  const [showDemographicsData, setShowDemographicsData] = useState(false);

  const normalizePatientRef = (value?: string) =>
    value?.replace(/[^a-zA-Z0-9]/g, "") ?? "";

  const viewedPatientRef = normalizePatientRef(patientId);
  const ownPatientRef = normalizePatientRef(user?.username);

  // Prontuário de dependente (Minha Família): não exibir edição do perfil do titular.
  const canEditProfile =
    access?.type === "patient" &&
    Boolean(ownPatientRef) &&
    (!viewedPatientRef || viewedPatientRef === ownPatientRef);

  const toggleSecondComponent = () => {
    setShowDemographicsData(!showDemographicsData);
  };
  const observationSummary = useFhirData?.extractPatientSummaryResource(
    summaryData,
    "Observation",
  )?.[0];

  const encounterSummary = useFhirData?.extractPatientSummaryResource(
    summaryData,
    "Encounter",
  )?.[0];

  const filterEncounter = useFhirData?.groupObservationsByEncounter(
    observationSummary?.entry,
    encounterSummary,
  );

  const lastObservations = lastEncounterObservationsData({
    encounters: filterEncounter,
    observations: ["8302-2", "55284-4", "29463-7"],
  });

  const { formatDate } = useDatetime;
  const { formatNameDisplay, formatGender, formatAge } = useFormatter;

  const auxAddress = patientData?.address?.[0];

  const contact = patientData?.telecom;
  const phone = contact?.find((e: any) => e.system === "phone")?.value;

  const phoneDescription = phone ? (
    <>
      <Stack
        height="22px"
        direction="row"
        alignItems="center"
        justifyContent="left"
        alignContent="center"
      >
        <Typography>{useFormatter.formatPhoneNumber(phone || "")}</Typography>

        {access?.type === "professional" ? (
          <Tooltip title={useFormatter.formatPhoneNumber(phone || "")}>
            <IconButton
              color="primary"
              onClick={() => {
                if (!phone) return;
                window.open(
                  `https://web.whatsapp.com/send?phone=+${useInternationalVerify.verifyPhoneDDI(
                    phone,
                  )}`,
                  "_blank",
                );
              }}
            >
              <WhatsApp />
            </IconButton>
          </Tooltip>
        ) : (
          <></>
        )}
      </Stack>
    </>
  ) : (
    "-"
  );

  const city = auxAddress?.city || "";
  const addressState = auxAddress?.state || "";
  const postalCode = auxAddress?.postalCode || "";
  const line = auxAddress?.line?.join(", ") || "";

  const address =
    [line, city, addressState, postalCode]
      ?.filter((el: any) => el)
      ?.join(" , ") || "-";

  const isVetApp = !currentFeatures.defaultApp;

  const animalExtension = isVetApp
    ? patientData?.extension?.find(
        (ext: any) =>
          ext.url === "http://hl7.org/fhir/StructureDefinition/patient-animal",
      )
    : undefined;

  const speciesExt = animalExtension?.extension?.find(
    (ext: any) => ext.url === "species",
  );
  const breedExt = animalExtension?.extension?.find(
    (ext: any) => ext.url === "breed",
  );
  const colorExt = animalExtension?.extension?.find(
    (ext: any) => ext.url === "animalColor",
  );
  const genderStatusExt = animalExtension?.extension?.find(
    (ext: any) => ext.url === "genderStatus",
  );

  const petSpeciesDisplay =
    speciesExt?.valueCodeableConcept?.coding?.[0]?.display ||
    speciesExt?.valueCodeableConcept?.coding?.[0]?.code ||
    undefined;

  const petBreedDisplay =
    breedExt?.valueCodeableConcept?.coding?.[0]?.display || undefined;

  const petColorDisplay =
    colorExt?.valueCodeableConcept?.coding?.[0]?.display || undefined;

  const petGenderStatusCode =
    genderStatusExt?.valueCodeableConcept?.coding?.[0]?.code || undefined;

  const petGenderStatusDisplay =
    genderStatusExt?.valueCodeableConcept?.coding?.[0]?.display || undefined;

  const petIdentifier =
    patientData?.identifier?.[0]?.value || patientData?.id || "-";

  const calculatePetAge = (birthDate?: string) => {
    if (!birthDate) return "-";
    const today = dayjs();
    const birth = dayjs(birthDate);
    const years = today.diff(birth, "year");

    if (years === 0) {
      const months = today.diff(birth, "month");
      return `${months} ${months === 1 ? "mês" : "meses"}`;
    }

    return `${years} ${years === 1 ? "ano" : "anos"}`;
  };
  type DemographicDataType = {
    [key: string]: {
      label: string;
      display: any;
    };
  };

  const auxType = {
    raceExists: userPatient?.extension?.find(
      (el: any) =>
        el.url ===
        "http://www.saude.gov.br/fhir/StructureDefinition/BRRacaCorEtnia-1.0",
    ),
    genderExists: userPatient?.extension?.find(
      (el: any) =>
        el.url ===
        "http://www.saude.gov.br/fhir/StructureDefinition/BRIdentidadeGenero",
    ),
    educationExists: userPatient?.extension?.find(
      (el: any) =>
        el.url === "http://terminology.hl7.org/ValueSet/v3-EducationLevel",
    ),
    religionExist: userPatient?.extension?.find(
      (el: any) =>
        el.url ===
        "http://terminology.hl7.org/ValueSet/v3-ReligiousAffiliation",
    ),
    maritalStatusExists: userPatient?.maritalStatus,
    occupationExists: userPatient?.extension?.find(
      (el: any) =>
        el.url === "https://terminologia.saude.gov.br/fhir/ValueSet/BROcupacao",
    ),
    familyIncomeExists: userPatient?.extension?.find(
      (el: any) =>
        el.url ===
        "http://www.saude.gov.br/fhir/StructureDefinition/BRRendaFamiliar",
    ),
  };

  const demographicData: DemographicDataType = isVetApp
    ? {}
    : {
        displayEducation: {
          label: "Escolaridade",
          display:
            auxType?.educationExists?.valueCodeableConcept?.coding?.[0]
              ?.display,
        },
        displayReligion: {
          label: "Religião",
          display:
            auxType?.religionExist?.valueCodeableConcept?.coding?.[0]?.display,
        },
        displayMaritalStatus: {
          label: "Estado civil",
          display: auxType?.maritalStatusExists?.coding?.[0]?.display,
        },
        displayOccupation: {
          label: "Profissão",
          display:
            auxType?.occupationExists?.valueCodeableConcept?.coding?.[0]
              ?.display,
        },
        displayFamilyIncome: {
          label: "Renda Familiar",
          display:
            auxType?.familyIncomeExists?.valueCodeableConcept?.coding?.[0]
              ?.display,
        },
      };

  const socialName =
    patientData?.name?.find((el: any) => el.use === "usual")?.text || "-";

  const auxData = isVetApp
    ? [
        {
          label: "Nome do pet",
          description: formatNameDisplay(patientData?.name?.[0]?.text || ""),
        },
        {
          label: "Espécie",
          description: petSpeciesDisplay || "-",
        },
        {
          label: "Raça",
          description: petBreedDisplay || "-",
        },
        {
          label: "Sexo",
          description:
            patientData?.gender === "male"
              ? "Macho"
              : patientData?.gender === "female"
                ? "Fêmea"
                : "-",
        },
        {
          label: "Castrado",
          description:
            petGenderStatusCode === "neutered"
              ? "Sim"
              : petGenderStatusCode === "intact"
                ? "Não"
                : petGenderStatusDisplay || "Desconhecido",
        },
        {
          label: "Idade",
          description: calculatePetAge(patientData?.birthDate),
        },
        {
          label: "Data de nascimento",
          description: patientData?.birthDate
            ? formatDate(`${patientData?.birthDate}`)
            : "-",
        },
        {
          label: "Cor",
          description: petColorDisplay || "-",
        },
      ]
    : [
        {
          label: "Nome do paciente",
          description: formatNameDisplay(patientData?.name?.[0]?.text || ""),
        },
        {
          label: "Nome social",
          description: formatNameDisplay(socialName),
        },
        {
          label: "Sexo de nascimento",
          description: formatGender(patientData?.gender) || "-",
        },
        {
          label: "Idade",
          description: patientData?.birthDate
            ? `${formatAge(`${patientData?.birthDate}`)} anos`
            : "-",
        },
        {
          label: "Telefone",
          description: phoneDescription,
        },
        {
          label: "Data de nascimento",
          description: patientData?.birthDate
            ? formatDate(`${patientData?.birthDate}`)
            : "-",
        },

        {
          label: "Endereço",
          description: address || "-",
        },
        {
          label: "Raça/Etnia",
          description:
            patientData?.extension?.find((ext: any) =>
              ext.url.includes("BRRacaCorEtnia"),
            )?.valueCodeableConcept?.coding?.[0]?.display || "-",
        },
        {
          label: "Identidade de gênero",
          description:
            auxType?.genderExists?.valueCodeableConcept?.coding?.[0]?.display,
        },
      ];

  useEffect(() => {
    async function fetchPatient() {
      const mpiResponse = currentFeatures.defaultApp
        ? await GetPatient({ identifier: patientId })
        : await GetVetPatient({ id: patientId });
      setPatientData(mpiResponse?.data);
    }

    if (patientId) fetchPatient();
  }, [patientId]);

  const displayName = formatNameDisplay(patientData?.name?.[0]?.text || "");
  const ageLabel = isVetApp
    ? calculatePetAge(patientData?.birthDate)
    : patientData?.birthDate
      ? `${formatAge(`${patientData?.birthDate}`)} anos`
      : undefined;

  const genderLabel = isVetApp
    ? patientData?.gender === "male"
      ? "Macho"
      : patientData?.gender === "female"
        ? "Fêmea"
        : undefined
    : formatGender(patientData?.gender) || undefined;

  const mobileMetaText = [ageLabel, genderLabel].filter(Boolean).join(" · ");

  const mobileInfoItems = isVetApp
    ? auxData
        .filter((item) =>
          ["Espécie", "Raça", "Idade", "Data de nascimento"].includes(
            item.label,
          ),
        )
        .map((item) => ({
          label: item.label,
          icon: item.label === "Data de nascimento" ? ("birth" as const) : undefined,
          value: (
            <Typography variant="body2" fontWeight={700}>
              {typeof item.description === "string" ? item.description : "—"}
            </Typography>
          ),
        }))
    : [
        {
          label: "Telefone",
          icon: "phone" as const,
          value: (
            <EhrPatientPhoneValue
              phone={phone}
              formattedPhone={useFormatter.formatPhoneNumber(phone || "")}
              showWhatsApp={access?.type === "professional"}
              onWhatsApp={() => {
                if (!phone) return;
                window.open(
                  `https://web.whatsapp.com/send?phone=+${useInternationalVerify.verifyPhoneDDI(
                    phone,
                  )}`,
                  "_blank",
                );
              }}
            />
          ),
        },
        {
          label: "Nascimento",
          icon: "birth" as const,
          value: (
            <Typography variant="body2" fontWeight={700}>
              {patientData?.birthDate
                ? formatDate(`${patientData?.birthDate}`)
                : "—"}
            </Typography>
          ),
        },
        ...(socialName && socialName !== "-"
          ? [
              {
                label: "Nome social",
                icon: "social" as const,
                value: (
                  <Typography variant="body2" fontWeight={700}>
                    {formatNameDisplay(socialName)}
                  </Typography>
                ),
              },
            ]
          : []),
        {
          label: "Endereço",
          icon: "address" as const,
          value: (
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ lineHeight: 1.45, wordBreak: "break-word" }}
            >
              {address && address !== "-" ? address : "—"}
            </Typography>
          ),
        },
      ];

  const mobileDemographics = !isVetApp
    ? Object.entries(demographicData)
        .filter(([, value]) => value?.display)
        .map(([, value]) => ({
          label: value.label,
          display: String(value.display),
        }))
    : [];

  if (isEhrMobile) {
    return (
      <EhrPatientHeaderMobile
        loading={Boolean(patientId) && !patientData}
        displayName={displayName}
        initials={patientInitials(displayName)}
        metaText={mobileMetaText || undefined}
        canEditProfile={canEditProfile}
        onEditProfile={() => navigate("/profile")}
        infoItems={mobileInfoItems}
        vitals={lastObservations}
        demographics={mobileDemographics}
        showDemographics={showDemographicsData}
        onToggleDemographics={
          mobileDemographics.length > 0 ? toggleSecondComponent : undefined
        }
      />
    );
  }

  return (
    <>
      {auxData?.map((el: any) => (
        <Grid
          item
          lg={3}
          md={3}
          sm={3}
          xs={6}
          key={`patient-information-${el.label}`}
        >
          <ReadOnlyField
            title={el.label}
            description={el.description}
            icon={false}
          />
        </Grid>
      ))}

      {!isVetApp &&
        Object.entries(demographicData)?.map(
          ([key, value]: [
            keyof DemographicDataType,
            DemographicDataType[keyof DemographicDataType],
          ]) => (
            <Grid
              item
              lg={3}
              md={3}
              sm={3}
              xs={6}
              key={`patient-information-${demographicData[key]}`}
              sx={{ display: showDemographicsData ? "block" : "none" }}
            >
              <ReadOnlyField
                title={demographicData?.[key]?.label}
                description={demographicData?.[key]?.display}
                icon={false}
              />
            </Grid>
          ),
        )}

      {!isVetApp && (
        <Grid item xs={12} sx={{ m: 0, p: 0 }}>
          <Stack direction="row" justifyContent={"center"} sx={{ mb: 0 }}>
            {showDemographicsData ? (
              <Tooltip title="Ver menos">
                <IconButton
                  onClick={toggleSecondComponent}
                  sx={{ "&:hover": { color: "primary700.main" } }}
                >
                  <ExpandLess />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Ver mais">
                <IconButton
                  onClick={toggleSecondComponent}
                  sx={{ "&:hover": { color: "primary700.main" } }}
                >
                  <ExpandMore />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>
      )}

      <Stack
        direction="row"
        alignItems={"end"}
        justifyContent={"space-between"}
        mt={2}
        paddingLeft={3}
        width={"100%"}
        flexWrap={"wrap"}
      >
        {lastObservations?.map((observation: any) => (
          <Tooltip
            title={
              <>
                <Stack
                  direction="row"
                  width="100%"
                  alignItems="center"
                  spacing={2}
                >
                  {observation?.practitioner?.professional && (
                    <Typography
                      color="neutral700.main"
                      sx={{
                        fontWeight: 500,
                        fontStyle: "italic",
                        fontSize: 12,
                      }}
                    >
                      Dr. {observation?.practitioner?.professional}
                    </Typography>
                  )}

                  <Chip
                    label={
                      observation?.practitioner?.healthcareService ||
                      "Sem especialidade"
                    }
                    size="small"
                    sx={{
                      color: "neutral500.main",

                      fontWeight: 500,
                      backgroundColor: "#f1f1f1",
                    }}
                  />
                </Stack>
                <Stack direction="row" width="100%" spacing={2} mt={1}>
                  <Typography
                    color="neutral700.main"
                    sx={{
                      fontSize: 12,
                    }}
                  >
                    {dayjs(observation?.date).format("DD/MM/YYYY")}
                  </Typography>
                </Stack>
              </>
            }
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [0, -14],
                    },
                  },
                ],
              },
              tooltip: {
                sx: {
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  borderRadius: "8px",
                  border: "0.5px solid #A9A9A9",
                  backdropFilter: "blur(4px)",
                  boxShadow: "4px 4px 16px 4px rgba(0, 0, 0, 0.1)",
                  color: "white",
                  fontSize: "1rem",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(255, 255, 255, 1)",
                },
              },
            }}
          >
            <Stack direction="column">
              <Stack direction="row" justifyContent="center" width="180px">
                <Typography
                  variant="body1"
                  color="neutral1000.main"
                  textAlign={"center"}
                  fontWeight={500}
                  sx={{ wordBreak: "break-word" }}
                >
                  {observation?.name}
                </Typography>
              </Stack>
              <Stack
                width="180px"
                borderRadius="8px"
                justifyContent="center"
                alignItems="center"
                padding={0.5}
                sx={{ background: "#E6F7F5" }}
              >
                <Typography
                  textAlign="center"
                  fontWeight={500}
                  sx={{ color: "neutral600.main" }}
                >
                  {`${observation?.value ?? "-"} ${observation?.unit ?? ""}`}
                </Typography>
              </Stack>
            </Stack>
          </Tooltip>
        ))}
      </Stack>
    </>
  );
}
